import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Container, Row, Col, Card, Form } from 'react-bootstrap';
import UnidadeViewModel from '../../viewmodel/UnidadeViewModel';
import useUnidadeStore from '../../stores/unidadeStore';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { BiPencil, BiTrash } from 'react-icons/bi';

const UnidadeEdicao: React.FC = () => {
    const { unidades, refreshUnidades } = useUnidadeStore();
    const [showEditModal, setShowEditModal] = useState(false);  // Controla exibição do modal de edição
    const [showDeleteModal, setShowDeleteModal] = useState(false);  // Controla exibição do modal de deleção
    const [unidadeSelecionada, setUnidadeSelecionada] = useState<any | null>(null);  // Armazena a unidade selecionada para edição ou deleção

    // Função para lidar com a edição de uma unidade
    const handleEdit = (unidade: any) => {
        setUnidadeSelecionada(unidade);
        setShowEditModal(true);
    };

    // Função para lidar com a deleção de uma unidade
    const handleDelete = (unidade: any) => {
        setUnidadeSelecionada(unidade);
        setShowDeleteModal(true);
    };

    // Função para confirmar deleção de uma unidade
    const confirmarDelecao = async () => {
        try {
            //await UnidadeViewModel.deletarUnidade(unidadeSelecionada.id);  // Chama a função de deleção no ViewModel
            setShowDeleteModal(false);
            await refreshUnidades();  // Atualiza a lista após a deleção
        } catch (error) {
            console.error('Erro ao deletar unidade:', error);
        }
    };

    // Função para lidar com o fechamento dos modais
    const handleCloseModal = () => {
        setShowEditModal(false);
        setShowDeleteModal(false);
        setUnidadeSelecionada(null);
    };

    useEffect(() => {
        refreshUnidades();  // Carrega as unidades na montagem do componente
    }, [refreshUnidades]);

    const validationSchema = Yup.object().shape({
        nome: Yup.string().required('O nome da unidade é obrigatório'),
        clientId: Yup.string()
    });

    return (
        <Container className="mt-5">
            <Row className="justify-content-md-center">
                <Col md={10}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title>Gerenciamento de Unidades/Órgãos</Card.Title>
                            <Table striped bordered hover style={{marginTop: '15px'}}>
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>ID do Cliente</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unidades.map((unidade) => (
                                        <tr key={unidade.id}>
                                            <td>{unidade.nome}</td>
                                            <td>{unidade.clientId}</td>
                                            <td>
                                                <Button className="btn btn-primary btn-sm me-2" onClick={() => handleEdit(unidade)}>
                                                    <BiPencil />
                                                </Button>                                                
                                                <Button
                                                    variant="btn btn-danger btn-sm me-2"
                                                    onClick={() => handleDelete(unidade)}
                                                >
                                                    <BiTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal de Edição */}
            <Modal show={showEditModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Editar Unidade/Órgão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {unidadeSelecionada && (
                        <Formik
                            initialValues={{
                                nome: unidadeSelecionada.nome,
                                clientId: unidadeSelecionada.clientId,
                            }}
                            validationSchema={validationSchema}
                            onSubmit={async (values, { setSubmitting }) => {
                                setSubmitting(true);
                                try {
                                    await UnidadeViewModel.atualizarUnidade(unidadeSelecionada.id, values);  // Chama função de atualização no ViewModel
                                    await refreshUnidades();  // Atualiza a lista após a edição
                                    setShowEditModal(false);
                                } catch (error) {
                                    console.error('Erro ao editar unidade:', error);
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                            }) => (
                                <Form noValidate onSubmit={handleSubmit}>
                                    <Form.Group controlId="nome">
                                        <Form.Label>Nome</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Nome da Unidade"
                                            value={values.nome}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isInvalid={touched.nome && !!errors.nome}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {typeof errors.nome === 'string' ? errors.nome : null}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="clientId" className="mt-3">
                                        <Form.Label>ID do Cliente</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="ID do Cliente"
                                            value={values.clientId}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isInvalid={touched.clientId && !!errors.clientId}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {typeof errors.clientId === 'string' ? errors.clientId : null}                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Button variant="primary" type="submit" className="mt-3" disabled={isSubmitting}>
                                        Salvar Alterações
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal de Deleção */}
            <Modal show={showDeleteModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Deletar Unidade/Órgão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza de que deseja deletar a unidade "{unidadeSelecionada?.nome}"?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={confirmarDelecao}>
                        Deletar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default UnidadeEdicao;
